import { useCallback, useEffect, useState } from "react";
import { HttpError, request } from "../../../api/http";
import { useConfirm } from "../../../components/Dialog";
import { IconClose } from "../../../components/Icons";
import { useAuth } from "../../../context/AuthContext";
import {
  ResizeHandle,
  useResizableDrawer,
} from "../../../hooks/useResizableDrawer";

type Props = {
  leadUlid: string;
  currentStatus: string;
  onClose: () => void;
  onApplied: () => void;
};

type DamageStatus = "ACCEPTED" | "FINISHED";
type CertificateAnswer = "yes" | "no";

type ClaimDto = {
  damageStatus: DamageStatus;
  notificationDate: string;
  deathCertificate: boolean;
  deceaseDate: string | null;
};

type Reason = { value: string; description: string };

type GetClaimResponse = {
  claim: ClaimDto | null;
  reason: string | null;
};

type ReasonsResponse = { reasons: Reason[] };

const today = (): string => {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

export default function ClaimDrawer({
  leadUlid,
  currentStatus,
  onClose,
  onApplied,
}: Props) {
  const { token } = useAuth();
  const confirm = useConfirm();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { width, startResize } = useResizableDrawer({ initial: 520, min: 360 });

  const [existingClaim, setExistingClaim] = useState<ClaimDto | null>(null);
  const [reasons, setReasons] = useState<Reason[]>([]);

  const [damageStatus, setDamageStatus] = useState<DamageStatus>("ACCEPTED");
  const [notificationDate, setNotificationDate] = useState<string>(today());
  const [deathCertificate, setDeathCertificate] = useState<CertificateAnswer>("no");
  const [deceaseDate, setDeceaseDate] = useState<string>("");
  const [reason, setReason] = useState<string>("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all([
      request<GetClaimResponse>(`/api/admin/operations/leads/${leadUlid}/claim`, {
        token,
      }),
      request<ReasonsResponse>(`/api/admin/operations/claim/finish-reasons`, {
        token,
      }),
    ])
      .then(([claimRes, reasonsRes]) => {
        if (cancelled) return;
        setReasons(reasonsRes.reasons);
        if (claimRes.claim) {
          setExistingClaim(claimRes.claim);
          setDamageStatus(claimRes.claim.damageStatus);
          setNotificationDate(claimRes.claim.notificationDate);
          setDeathCertificate(claimRes.claim.deathCertificate ? "yes" : "no");
          setDeceaseDate(claimRes.claim.deceaseDate ?? "");
          setReason(claimRes.reason ?? "");
        }
      })
      .catch((err) => {
        if (cancelled) return;
        setError(
          err instanceof HttpError
            ? `HTTP ${err.status}`
            : "No se pudo cargar el claim",
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [leadUlid, token]);

  const needsReason = damageStatus === "FINISHED" && deathCertificate === "no";

  const validate = useCallback((): string | null => {
    if (!notificationDate) return "La fecha de notificación es obligatoria";
    if (deceaseDate && deceaseDate < notificationDate) {
      return "La fecha de fallecimiento no puede ser anterior a la notificación";
    }
    if (needsReason && !reason) {
      return "Selecciona un motivo de finalización";
    }
    return null;
  }, [notificationDate, deceaseDate, needsReason, reason]);

  const submit = async () => {
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }
    const isCreate = existingClaim === null;
    const ok = await confirm({
      title: isCreate ? "Crear claim" : "Actualizar claim",
      message: isCreate
        ? "Esto creará el claim para este lead y cambiará su estado a 'claim'. ¿Continuar?"
        : "Esto actualizará los datos del claim. ¿Continuar?",
      confirmLabel: isCreate ? "Crear" : "Actualizar",
      tone: isCreate ? "warning" : "primary",
    });
    if (!ok) return;

    setSubmitting(true);
    setError(null);
    try {
      const body = {
        damageStatus,
        notificationDate,
        deathCertificate,
        deceaseDate: deceaseDate || null,
        reason: needsReason ? reason : null,
      };
      await request(`/api/admin/operations/leads/${leadUlid}/claim`, {
        method: isCreate ? "POST" : "PUT",
        token,
        body,
      });
      if (isCreate && currentStatus !== "claim") {
        await request(`/api/admin/operations/onboarding/${leadUlid}/status`, {
          method: "PATCH",
          token,
          body: { status: "claim" },
        });
      }
      onApplied();
    } catch (err) {
      setError(
        err instanceof HttpError
          ? `HTTP ${err.status}: ${
              ((err.body as { message?: string })?.message ?? "").slice(0, 200) ||
              ((err.body as { error?: string })?.error ?? "")
            }`
          : "No se pudo guardar el claim",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const title = existingClaim === null ? "Crear claim" : "Editar claim";

  return (
    <DrawerShell
      title={title}
      onClose={onClose}
      width={width}
      onStartResize={startResize}
    >
      {loading ? (
        <div style={{ color: "#73879C", fontSize: ".85rem" }}>Cargando…</div>
      ) : (
        <>
          <Field label="Estado del siniestro">
            <select
              value={damageStatus}
              onChange={(e) => setDamageStatus(e.target.value as DamageStatus)}
              style={selectStyle}
              disabled={submitting}
            >
              <option value="ACCEPTED">En proceso</option>
              <option value="FINISHED">Finalizado</option>
            </select>
          </Field>

          <Field label="Fecha de notificación">
            <input
              type="date"
              value={notificationDate}
              onChange={(e) => setNotificationDate(e.target.value)}
              style={inputStyle}
              disabled={submitting}
            />
          </Field>

          <Field label="¿Tiene certificado de defunción?">
            <select
              value={deathCertificate}
              onChange={(e) =>
                setDeathCertificate(e.target.value as CertificateAnswer)
              }
              style={selectStyle}
              disabled={submitting}
            >
              <option value="yes">Sí</option>
              <option value="no">No</option>
            </select>
          </Field>

          <Field label="Fecha de fallecimiento">
            <input
              type="date"
              value={deceaseDate}
              onChange={(e) => setDeceaseDate(e.target.value)}
              style={inputStyle}
              disabled={submitting}
            />
          </Field>

          {needsReason && (
            <Field label="Motivo de finalización (sin certificado)">
              <select
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                style={selectStyle}
                disabled={submitting}
              >
                <option value="">— Selecciona un motivo —</option>
                {reasons.map((r) => (
                  <option key={r.value} value={r.value}>
                    {r.description}
                  </option>
                ))}
              </select>
            </Field>
          )}

          {error && <ErrorBox>{error}</ErrorBox>}

          <DrawerFooter
            onClose={onClose}
            onSubmit={submit}
            submitting={submitting}
            submitLabel={existingClaim === null ? "Crear claim" : "Guardar"}
          />
        </>
      )}
    </DrawerShell>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label style={{ display: "grid", gap: "0.3rem", marginBottom: "0.85rem" }}>
      <span
        style={{
          fontSize: ".72rem",
          color: "#73879C",
          fontWeight: 600,
          textTransform: "uppercase",
          letterSpacing: ".3px",
        }}
      >
        {label}
      </span>
      {children}
    </label>
  );
}

function DrawerShell({
  title,
  onClose,
  children,
  width,
  onStartResize,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  width: number;
  onStartResize: (event: React.MouseEvent) => void;
}) {
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(15, 23, 42, 0.55)",
        zIndex: 50,
        display: "flex",
        justifyContent: "flex-end",
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width,
          height: "100%",
          background: "white",
          boxShadow: "-2px 0 16px rgba(0,0,0,.18)",
          display: "flex",
          flexDirection: "column",
          position: "relative",
          whiteSpace: "normal",
          wordBreak: "break-word",
          textAlign: "left",
        }}
      >
        <ResizeHandle onMouseDown={onStartResize} />
        <header
          style={{
            display: "flex",
            alignItems: "center",
            padding: "0.95rem 1.1rem",
            borderBottom: "1px solid #e9ecef",
            gap: "0.75rem",
          }}
        >
          <h2 style={{ margin: 0, fontSize: "1rem", color: "#1f2933", flex: 1 }}>
            {title}
          </h2>
          <button type="button" onClick={onClose} style={closeBtn}>
            <IconClose />
          </button>
        </header>
        <div style={{ flex: 1, overflow: "auto", padding: "1rem 1.1rem" }}>
          {children}
        </div>
      </div>
    </div>
  );
}

function DrawerFooter({
  onClose,
  onSubmit,
  submitting,
  submitLabel,
}: {
  onClose: () => void;
  onSubmit: () => void;
  submitting: boolean;
  submitLabel: string;
}) {
  return (
    <div
      style={{
        display: "flex",
        gap: "0.5rem",
        justifyContent: "flex-end",
        marginTop: "1.1rem",
      }}
    >
      <button
        type="button"
        onClick={onClose}
        style={ghostBtn}
        disabled={submitting}
      >
        Cancelar
      </button>
      <button
        type="button"
        onClick={onSubmit}
        style={primaryBtn}
        disabled={submitting}
      >
        {submitting ? "Guardando…" : submitLabel}
      </button>
    </div>
  );
}

function ErrorBox({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        marginTop: "0.7rem",
        padding: "0.5rem 0.7rem",
        borderRadius: "0.4rem",
        background: "#fde2e2",
        color: "#b32424",
        fontSize: ".8rem",
      }}
    >
      {children}
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  width: "100%",
  padding: "0.45rem 0.6rem",
  fontSize: ".88rem",
  border: "1px solid #dee2e6",
  borderRadius: "0.4rem",
  background: "#f8f9fb",
};

const selectStyle: React.CSSProperties = {
  ...inputStyle,
  appearance: "auto",
};

const closeBtn: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: "2rem",
  height: "2rem",
  padding: 0,
  borderRadius: "0.4rem",
  background: "#f8f9fb",
  color: "#1f2933",
  border: "1px solid #dee2e6",
  cursor: "pointer",
};

const ghostBtn: React.CSSProperties = {
  padding: "0.45rem 0.95rem",
  borderRadius: "0.4rem",
  background: "white",
  color: "#73879C",
  border: "1px solid #dee2e6",
  fontSize: ".82rem",
  fontWeight: 600,
  cursor: "pointer",
};

const primaryBtn: React.CSSProperties = {
  padding: "0.45rem 1.1rem",
  borderRadius: "0.4rem",
  background: "#0069d9",
  color: "white",
  border: "1px solid #0069d9",
  fontSize: ".82rem",
  fontWeight: 600,
  cursor: "pointer",
};
